import React, { useContext, useEffect, useState } from 'react';
import {
   Accordion, AccordionBody, AccordionHeader, AccordionItem, Button, ButtonGroup, Col,
   Collapse, Input, List, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import Switch from "react-switch";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { MdArrowRight, MdOutlineExpandLess, MdOutlineExpandMore } from "react-icons/md";
import _ from 'lodash';
import { ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, dateHelpers, storage, UserContext } from '../utils';

export default function TransferEdit(props) {
   const ICONS = {
      collapseIcon: <MdOutlineExpandLess className="mobileToggle align-self-center" />,
      expandIcon: <MdOutlineExpandMore className="mobileToggle align-self-center" />,
      arrowIcon: <MdArrowRight className="mobileToggleSmall align-self-center" />
   }
   const { id } = useParams();
   const { currentUser } = useContext(UserContext);
   const [barcode, setBarcode] = useState(null);
   const [canEdit, setEdit] = useState(false);
   const [data, setData] = useState({});
   const [expanded, setExpanded] = useState("0");
   const [initWgt, setInitWgt] = useState(true);
   const [lines, setLines] = useState({});
   const [loaded, setLoaded] = useState(false);
   const [manualEntry, setManual] = useState(null);
   const [needsWgt, setNeedsWgt] = useState(false);
   const [open, setOpen] = useState(null);
   const [printPDF, setPrintPDF] = useState(false);
   const [showDetail, setShow] = useState(true);
   const [showWgt, setShowWgt] = useState(false);
   const [workCase, setWorkCase] = useState({});
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   const navigate = useNavigate();
   let modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";

   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const isMobile = savedDevice.isMobile;
   const hasScanner = savedDevice.hasScanner;
   const handlePrint = nextChecked => { setPrintPDF(nextChecked); }

   useEffect(() => {
      if (!loaded && id) {
         api
            .post(`Transfer/GetTransfer/${id}`)
            .then(response => {
               if (response.data.success) {
                  setData(response.data.xfer.transfer);
                  setLines(response.data.xfer.lineItems);
                  setWorkCase(response.data.workCase);
                  setEdit(response.data.xfer.canEdit);
                  setShow(true);
               }
               else {
                  toast.warning(response.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setLoaded(true);
            })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id])

   useEffect(() => {
      // if device has built-in scanner see if a scan was completed
      if (barcode && hasScanner && !manualEntry) {
         setLoaded(false);
      }
      if (manualEntry)
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [barcode])

   useEffect(() => {
      // This just sets focus for the first page load
      if (canEdit) {
         inputFocus("searchInput");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [canEdit])

   useEffect(() => {
      if (!loaded && barcode) {
         // If a QR code was scanned then parse out just the barcode from the data
         var code = (barcode.length > 20 && barcode.toUpperCase().substring(0, 8) === "TRANSFER")
            ? barcode.substring(15, 27) // TransferLabel, IV0123456789, {... remaining QR Code contents}
            : barcode;

         api
            .post(`Transfer/GetTransferCase/${data.transferId}/${code}`)
            .then(response => {
               if (response.data.success) {
                  setNeedsWgt(true);
                  setInitWgt(true);
                  setWorkCase(response.data.workCase);
               }
               else {
                  toast.warning(response.data.message);
                  inputFocus("searchInput");
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
               inputFocus("searchInput");
            })
            .finally(() => {
               setLoaded(true);
            });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded])

   useEffect(() => {
      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }

      if (!barcode) {
         inputFocus("searchInput");
         return;
      }

      setLoaded(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   useEffect(() => {
      if (workCase && needsWgt) {
         setBarcode(null);
         setShowWgt(true);
         if (initWgt === true) {
            inputFocus("lblWgt");
            setInitWgt(false);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [workCase])

   function addCase() {
      if (workCase.weight <= 0 || workCase.initialWeight < workCase.weight) {
         toast.error('Not a valid weight. Please enter a valid weight.');
         return;
      }

      setLoaded(false);
      api
         .post(`Transfer/AddTransferCase/`, workCase)
         .then((response) => {
            if (response.data.success) {
               if (data.fromSite === "NEWTRANSFER") {  // this is the very first case added to transfer
                  setData(response.data.xfer.transfer); // this corrects the missing "from" site
               }
               setLines(response.data.xfer.lineItems);
               setNeedsWgt(false);
               setInitWgt(true);
               setShowWgt(false);
               setWorkCase(response.data.workCase);
            } else {
               toast.warning(response.data.message);
               clearCase();
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setLoaded(true);
            inputFocus("searchInput");
         });
   }

   function clearCase() {
      setNeedsWgt(false);
      setInitWgt(true);
      setShowWgt(false);
      setBarcode(null);

      let theCase = Object.assign({}, workCase);
      theCase["barcode"] = 0;
      theCase["caseDesc"] = "";
      theCase["caseNumber"] = "";
      theCase["item"] = "";
      theCase["splitCase"] = false;
      theCase["weight"] = 0;
      setWorkCase(theCase);
   }

   function close() {
      navigate(constants.ENDPOINTS.transfers);
   }

   function completeTransfer() {
      if (!lines || lines.length < 1) {
         toast.error("You cannot complete a transfer with no line items. You can delete this transfer from the main transfer screen.");
         inputFocus("searchInput");
         return;
      }

      var url = `Transfer/CompleteTransfer/${data.transferId}/${printPDF}`
      var successMsg = "Transfer Complete. Be sure to place any new inventory labels on the transferred items and to turn in the transfer report.";
      setLoaded(false);

      if (printPDF) {
         var displayId = ("000000" + data.transferId).slice(-6);
         api
            .postDownloadBinary(url, {},
               constants.MIME_TYPES.pdf,
               `TR-${displayId}_${dateHelpers.getDateStringForReportName()}.pdf`
            )
            .then((res) => {
               // The success toast and close() used to sit in a SECOND chained .then, so they ran even
               // when the call failed - the user was told "Transfer Complete, be sure to place any new
               // inventory labels" over the top of the error, and was navigated away from the screen.
               // One .then with an early return instead. 
               if (res?.data === false) {
                  toast.error(res.message);
                  return;
               }
               toast.success(successMsg);
               close();
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            // NOTE - REQUIRED once the failure path returns early. completeTransfer sets loaded=false to raise
            // the spinner, and close() only navigates - so before, a failure still unmounted the component
            // and the spinner state stopped mattering. Staying on the screen means loaded has to be put
            // back or the user is left watching a spinner that never stops behind a toast that fades.
            // Matches the non-PDF branch below, which has always done this.
            .finally(() => {
               setLoaded(true);
               inputFocus("searchInput");
            })
      }
      else {
         api
            .post(url)
            .then(response => {
               if (response.data.success) {
                  toast.success(successMsg);
                  close();
               }
               else {
                  toast.warning(response.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setLoaded(true);
               inputFocus("searchInput");
            })
      }
   }

   function deleteCase(barcode, desc) {
      confirmAlert({
         title: "Please Confirm",
         message: `Are you sure you want to remove this case from the transfer? ${desc}?`,
         buttons: [
            {
               label: "Yes",
               onClick: () => {
                  setLoaded(false);
                  api
                     .post(`Transfer/DeleteTransferCase/${data.transferId}/${barcode}`)
                     .then((response) => {
                        if (response.data.success) {
                           setLines(response.data.xfer.lineItems);
                           setWorkCase(response.data.workCase);
                        } else {
                           toast.warning(response.data.message);
                        }
                     })
                     .catch((error) => {
                        api.catchHandler(error);
                        toast.error(error);
                     })
                     .finally(() => {
                        setLoaded(true);
                        inputFocus("searchInput");
                     });
               },
            },
            {
               label: "No",
               onClick: () => {
                  inputFocus("searchInput");
               }
            },
         ],
      });
   }

   const handleFocus = (event) => event.target.select();

   const handleKeypress = e => {
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(null);
         return;
      }
      inputFocus("manualInput");
   };

   async function inputFocus(fieldname) {
      await sleep(80);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   async function lookup(barcode) {
      setBarcode(barcode);

      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }
      inputFocus("searchInput");
   }

   function onCaseChange(field, value) {
      // Changes to the weight of the case in process
      if (workCase.initialWeight < value) {
         value = workCase.initialWeight;
         toast.info('You cannot exceed the original weight');
      }

      let changes = Object.assign({}, workCase);
      changes[field] = value;
      if (workCase.initialWeight !== value) {
         changes['splitCase'] = false;
      }
      setWorkCase(changes);
   }

   const toggle = (idx) => {
      if (expanded === idx) {
         setExpanded();
      } else {
         setExpanded(idx);
      }

      if (canEdit) {
         inputFocus("searchInput");
      }
   };

   function toggleSplit() {
      let changes = Object.assign({}, workCase);
      changes["splitCase"] = !workCase.splitCase;
      setWorkCase(changes);
   }

   return (
      <>
         <DesktopContainer screenName="Inventory Transfer">
            <ServiceThreadSpinner on={!loaded}>
               {loaded && data && data.transferId > 0 ?
                  <div>
                     {canEdit ?
                        <div>
                           <Row><Col className="h6 fw-bold" xs="12">Scan an inventory label to begin</Col></Row>
                           <Row><Col><hr className="mb-0 my-1" /></Col></Row>
                           <br />
                           {!hasScanner && isMobile ?
                              <div>
                                 <Row className="justify-content-center">
                                    <Col xs="12">
                                       <Button
                                          title="scan entry"
                                          className="shredButton w-100 btn-md"
                                          onClick={() => setOpen(1)}>
                                          Scan Barcode
                                       </Button>
                                    </Col>
                                 </Row>
                                 <Row className="lineheight8">&nbsp;</Row>
                              </div>
                              : null
                           }
                           <div>
                              <Row className="justify-content-center">
                                 <Col xs="12">
                                    <Button
                                       title="manual entry"
                                       className="shredButton w-100 btn-md"
                                       onClick={() => setManual(1)}>
                                       Manual Entry
                                    </Button>
                                 </Col>
                              </Row>
                              {isMobile ?
                                 <div>
                                    <Row className="lineheight8">&nbsp;</Row>
                                    <Row className="justify-content-center">
                                       <Col xs="12">
                                          <Input
                                             name="searchInput"
                                             placeholder="Focus here to scan barcode"
                                             className="h-100"
                                             onChange={(e) => lookup(e.target.value)}
                                          />
                                       </Col>
                                    </Row>
                                 </div>
                                 : null
                              }
                           </div>
                           <br />
                        </div>
                        : null
                     }

                     <Collapse className="mx-0 py-0 px-0" isOpen={showDetail}>
                        <List type="unstyled">
                           <li className="fw-bold my-0 px-2">{data.title}</li>
                           {!lines || lines.length < 1 ?
                              <li className="mobileBlueBg text-white my-0 py-3 px-2">No items have yet been scanned.</li>
                              : null
                           }
                        </List>

                        {lines && lines.length > 0 ?
                           <div>
                              <Accordion open={expanded} toggle={toggle}>
                                 {_.map(lines, (item, index) => {
                                    return (
                                       <div details={item} key={index} >
                                          <AccordionItem className={(index % 2 === 0) ? "mobileBlueBg text-white my-0 py-1 px-1" : "mobileBlueBgAlt text-white my-0 py-1 px-1"}>
                                             <AccordionHeader targetId={String(index)}>
                                                {item.itemDesc}
                                             </AccordionHeader>
                                             <AccordionBody accordionId={String(index)} className="py-1 px-0">
                                                {item.cases && item.cases.length > 0 ?
                                                   <div>
                                                      {_.map(item.cases, (lot, idx) => {
                                                         return (
                                                            <div details={lot} key={idx} className="py-1 px-0">
                                                               {ICONS.arrowIcon} {lot.caseDesc}&nbsp;&nbsp;
                                                               {canEdit ?
                                                                  <Button children="Remove"
                                                                     className="btn btn-sm px-1 py-0"
                                                                     onClick={() => { deleteCase(lot.barcode, lot.caseDesc); }} />
                                                                  : null
                                                               }
                                                            </div>
                                                         )
                                                      })}
                                                   </div>
                                                   : null
                                                }
                                             </AccordionBody>
                                          </AccordionItem>
                                       </div>
                                    )
                                 })}
                              </Accordion>
                           </div>
                           : null
                        }
                     </Collapse>
                  </div>
                  : null
               }

               <Row className="lineheight8">&nbsp;</Row>
               {loaded && lines && canEdit && lines.length > 0 ?
                  <div>
                     {!isMobile ? /* This should only show when on a large device */
                        <Row>
                           <Col xs="12">
                              <label>
                                 <span className="my-1">&nbsp; &nbsp; &nbsp; &nbsp; {printPDF ? "Download as PDF" : "Print to printer"}  </span>
                                 <Switch
                                    onChange={handlePrint}
                                    checked={printPDF}
                                    className="react-switch"
                                 />
                              </label>
                           </Col>
                        </Row>
                        : null
                     }
                     <Row>
                        <Col xs="12">
                           <Button
                              title="Save"
                              className="shredButton w-100 btn-md my-0"
                              onClick={() => completeTransfer()}>
                              Complete Transfer
                           </Button>
                        </Col>
                     </Row>
                     <Row className="lineheight8">&nbsp;</Row>
                  </div>
                  : null
               }
               <Row>
                  <Col xs="12">
                     <Button
                        title="To Search"
                        className="btn w-100 btn-md my-0"
                        onClick={() => close()}>
                        Close
                     </Button>
                  </Col>
               </Row>



               <Modal isOpen={manualEntry !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Enter Barcode" />
                  <ModalBody
                     children={
                        <Input
                           name="manualInput"
                           placeholder="Enter barcode"
                           className="h-100"
                           onChange={(e) => lookup(e.target.value)}
                           onKeyPress={handleKeypress}
                        />
                     }
                  />
                  <ModalFooter>
                     <ButtonGroup>
                        <Button children="Retrieve" className="button-success" onClick={() => {
                           setManual(null);
                        }} />
                        <Button children="Cancel" className="button-danger" onClick={() => {
                           setBarcode(null);
                           setManual(null);
                        }} />
                     </ButtonGroup>
                  </ModalFooter>
               </Modal>


               <Modal isOpen={open !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Scan a Barcode" />
                  <ModalBody
                     children={
                        <BarcodeScannerComponent
                           height={350}
                           width={350}
                           onUpdate={(err, result) => {
                              if (result) {
                                 setBarcode(result.text);
                                 setOpen(null);
                                 setLoaded(false);
                                 inputFocus("searchInput");
                              }
                              else {
                                 setBarcode(null);
                              }
                           }}
                           // onError={(err) => console.error("error", err)}
                           facingMode={"environment"}
                        />
                     }
                  />
                  <ModalFooter>
                     <Button
                        className="shredButton ms-7"
                        onClick={() => {
                           setOpen(null);
                           inputFocus("searchInput");
                        }}>Cancel
                     </Button>
                  </ModalFooter>
               </Modal>


               {loaded && workCase ?
                  <Modal isOpen={showWgt !== false} contentClassName={modalClassName}>
                     <ModalHeader className="mobileBlueBg text-white" children="Transfer Amount" />
                     <ModalBody className="mobileBlueBgAlt text-white"
                        children={
                           <Table borderless size="sm">
                              <tbody>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="h6 text-white fw-bold my-0">{workCase.item}</label>
                                    </td>
                                 </tr>
                                 <tr><td className="data-cell m-0 p-0 lineheight8">&nbsp;</td></tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-white h6 my-1">LBS to Transfer:</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <Input
                                          name="lblWgt"
                                          type="number"
                                          value={workCase.weight}
                                          className="h-100"
                                          onFocus={handleFocus}
                                          onChange={(e) => { onCaseChange("weight", e.target.value) }}
                                          placeholder="Enter amount ..."
                                       />
                                    </td>
                                 </tr>
                                 <tr><td className="data-cell m-0 p-0 lineheight8">&nbsp;</td></tr>
                                 <tr>
                                    <td colSpan="2" className="data-cell my-0 py-0">
                                       <label className="text-white h6">
                                          <input type="checkbox"
                                             checked={workCase.splitCase}
                                             disabled={workCase.initialWeight !== workCase.weight}
                                             className="me-2"
                                             onChange={(e) => toggleSplit(e.target.checked)} />
                                          Return item as complete case
                                       </label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td colSpan="2" className="data-cell my-0 py-0">
                                       <label className="text-white h6">(IE returning a partly consumed item from the plant back to the warehouse. No additional labels are produced.)</label>
                                    </td>
                                 </tr>
                                 <tr><td className="data-cell m-0 p-0 lineheight8">&nbsp;</td></tr>
                                 <tr>
                                    <td className="data-cell my-0 mx-0 p-0">
                                       <Button
                                          className="shredButton w-100 btn-md my-0"
                                          onClick={() => {
                                             addCase();
                                          }}>
                                          OK
                                       </Button>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 mx-0 p-0">
                                       <Button
                                          className="btn w-100 btn-md mt-2 mb-0"
                                          onClick={() => {
                                             clearCase();
                                          }}>
                                          Cancel
                                       </Button>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        }
                     />
                  </Modal>
                  : null
               }

            </ServiceThreadSpinner>
         </DesktopContainer >
      </>
   );
}
